import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense, Component } from 'react'
import { useUnifiedWallet } from '@jup-ag/wallet-adapter'
import { isPaperActive } from '../utils/paperTrade'
import { useLocalWallet } from './WalletContext'

// Privy — and its enormous transitive weight (viem/EVM, walletconnect,
// coinbase-sdk) — behind a lazy() boundary. Rollup puts the whole thing in a
// separate chunk that loads AFTER first paint, so the chart isn't waiting on it.
// See PrivyRuntime.jsx for the full rationale.
//
// BLOCKER RESILIENCE. A bare lazy() throws during render when its chunk can't
// be fetched — and nothing caught it, so React unmounted the whole tree: white
// screen. That is not hypothetical. The emitted filename contained the vendor
// name, which matches ad-filter lists (uBlock, Brave shields, Opera's blocker,
// filtering DNS); the request was blocked, the SPA fallback returned
// index.html, strict MIME checking rejected it, and the ENTIRE terminal died
// over a wallet chunk. Charts, heatmap, tape and bot need none of it.
//
// Three layers now: (1) the import resolves to a null component on failure
// instead of throwing, (2) an error boundary below catches anything that still
// escapes — including the SDK throwing at runtime when auth.privy.io itself is
// filtered, and (3) the user is TOLD, once, instead of guessing.
// vite.config.js also rewrites the chunk name so the common case never fires.
let notifyPrivyBlocked = null
// Retry before surrendering: a single flaky fetch on mobile data used to
// declare the runtime blocked for the WHOLE session (lazy() caches its
// rejection). Two spaced retries distinguish "blocker" from "bad signal".
const importPrivyWithRetry = (attempt = 0) =>
  import('./PrivyRuntime').catch((err) => {
    if (attempt < 2) return new Promise((res) => setTimeout(res, 1500 * (attempt + 1)))
      .then(() => importPrivyWithRetry(attempt + 1))
    console.warn('[privy] runtime chunk unavailable after retries — wallet features disabled, app continues', err)
    notifyPrivyBlocked?.()
    return { default: () => null }
  })
const PrivyRuntime = lazy(importPrivyWithRetry)

// Catches throws from the lazy subtree so they can never reach the root.
class PrivyErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false } }
  static getDerivedStateFromError() { return { failed: true } }
  componentDidCatch(err) {
    console.warn('[privy] runtime failed — wallet features disabled, app continues', err)
    this.props.onFail?.()
  }
  render() { return this.state.failed ? null : this.props.children }
}

// The shape usePrivyWallet() returns before Privy has loaded / logged in. This
// is what makes the deferral invisible: PrivyWalletButton already renders a
// disabled "…" when ready is false, so the UI has always had this state — it
// just used to flash for a millisecond instead of a couple of seconds.
const PRIVY_STUB = {
  ready: false,
  authenticated: false,
  address: null,
  publicKey: null,
  signTransaction: async () => { throw new Error('Bot wallet still loading') },
  exportWallet: async () => { throw new Error('Bot wallet still loading') },
  isReady: false,
  login: () => {},
  logout: () => {},
}

// Blocked ≠ loading. PRIVY_STUB has ready:false, which makes the wallet button
// spin "…" forever — an honest state while loading, a lie once we know the
// runtime is never coming. This settles the UI (ready) while keeping isReady
// false so nothing can attempt to sign.
const PRIVY_BLOCKED = { ...PRIVY_STUB, ready: true }

const DualWalletContext = createContext(null)

// Wallet modes
export const WALLET_MODE = {
  BROWSER: 'browser',  // Phantom, Solflare, etc. (with confirmations)
  LOCAL: 'local',       // Encrypted keypair (instant, no confirms)
  PRIVY: 'privy',       // Email-login embedded bot wallet (user-owned, exportable)
}

export function DualWalletProvider({ children, privyDropSignal = 0 }) {
  const browser = useUnifiedWallet()
  const local = useLocalWallet()

  // Privy is RECEIVED, not called. usePrivyWallet() throws without a
  // <PrivyProvider> ancestor, and providing that ancestor at the top is exactly
  // what forced the whole SDK into the entry bundle. So instead: hold the wallet
  // in state (stub until it arrives), and let a lazily-mounted PrivyRuntime pump
  // the real one up once its chunk has loaded. The app renders immediately with
  // the stub; the wallet fills in a beat later.
  const [privy, setPrivy] = useState(PRIVY_STUB)

  // WHEN Privy boots matters as much as WHERE its bytes live. lazy() moved the
  // SDK out of the entry bundle — but mounting <PrivyRuntime> unconditionally
  // fired its import() on the app's FIRST render, so the chunk download, the
  // viem/walletconnect evaluation, Privy's embedded-wallet iframe and its
  // session restore all landed in the exact seconds the chart was opening its
  // WebSocket and painting first frames. Same boot-window contention, just
  // from a different file — the load lag the lazy split was supposed to kill.
  // Boot it when the main thread goes IDLE instead: requestIdleCallback with a
  // 4s ceiling, setTimeout fallback where rIC doesn't exist (iOS Safari). The
  // chart wins the radio and the CPU; the wallet button sits at its existing
  // "…" state a couple seconds longer, which is exactly the trade we want.
  const [privyBlocked, setPrivyBlocked] = useState(false)
  const [blockNoticeDismissed, setBlockNoticeDismissed] = useState(false)
  const markBlocked = useCallback(() => {
    setPrivyBlocked(true)
    setPrivy(PRIVY_BLOCKED)     // settle the button out of its loading state
  }, [])
  // The lazy factory lives at module scope and can't setState directly, so it
  // calls through this slot while a provider is mounted.
  useEffect(() => {
    notifyPrivyBlocked = markBlocked
    return () => { notifyPrivyBlocked = null }
  }, [markBlocked])

  const [bootPrivy, setBootPrivy] = useState(false)
  // Stable callback so the pump's effect doesn't refire every render.
  const onPrivyWallet = useCallback((w) => setPrivy(w || PRIVY_STUB), [])

  // Persist preference. Normalize unrecognized/stale stored values to BROWSER so
  // the resolvers, PhoneConnect, and WalletArea can't disagree on a bad value.
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem('solcheddar_wallet_mode')
    return Object.values(WALLET_MODE).includes(stored) ? stored : WALLET_MODE.BROWSER
  })

  useEffect(() => {
    localStorage.setItem('solcheddar_wallet_mode', mode)
  }, [mode])

  // (Lives BELOW the mode declaration on purpose — the gate reads `mode`,
  // and hoisting it above is the same temporal-dead-zone trap the wake
  // reconnect effect documents. Order matters in hooks.)
  // On-demand ignition for the paths below that DON'T auto-boot. Exposed on
  // the context so PrivyWalletButton can start the runtime the moment the
  // user actually reaches for it.
  const requestPrivyBoot = useCallback(() => setBootPrivy(true), [])
  useEffect(() => {
    // MODE GATE. Idle-boot solved WHEN the runtime loads; this solves WHETHER.
    // Once mounted, Privy's style engine rewrites stylesheet rules for the
    // whole session — a profile showed ~1.3s of an 11s futures session going
    // to deleteRule/insertRule + the style recalcs they trigger, in a session
    // where the Hyperliquid wallet does all the signing and Privy contributes
    // nothing. So: auto-boot (idle-deferred, exactly as before) ONLY when the
    // stored wallet mode is PRIVY — that user's session restore must not wait
    // for a click. Every other mode pays zero until the user opens the Privy
    // login flow, which boots it via requestPrivyBoot. Switching mode to
    // PRIVY mid-session also lands here and boots.
    if (bootPrivy) return
    if (mode !== WALLET_MODE.PRIVY) return
    const go = () => setBootPrivy(true)
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(go, { timeout: 4000 })
      return () => window.cancelIdleCallback(id)
    }
    const t = setTimeout(go, 2500)
    return () => clearTimeout(t)
  }, [mode, bootPrivy])

  // Resolve the active publicKey based on mode
  const publicKey = useMemo(() => {
    if (mode === WALLET_MODE.LOCAL) {
      return local.publicKey && !local.isLocked ? local.publicKey : null
    }
    if (mode === WALLET_MODE.PRIVY) {
      return privy.isReady ? privy.publicKey : null
    }
    return browser.publicKey || null
  }, [mode, local.publicKey, local.isLocked, privy.isReady, privy.publicKey, browser.publicKey])

  // Latest wallet objects, read at CALL time instead of captured as deps.
  //
  // signTransaction used to depend on [mode, local, privy, browser] — the whole
  // objects. `browser` is useUnifiedWallet()'s return and `local` is
  // useLocalWallet()'s, both rebuilt every render, so signTransaction got a new
  // identity every render. It is passed to TradingProvider, where
  // executeMarketOrder / handleOrderConfirm / handleClosePosition /
  // handleCancelLimitOrder / handleMoveLimitOrder all list it as a dependency —
  // so all five churned, useTrading's memo recomputed, the TradingContext value
  // changed identity ~32x/s, and EVERY consumer re-rendered. Context updates
  // bypass React.memo, so the memoized FootprintChart re-rendered ~32x/s with
  // identical props and no state of its own changing. Measured: ctxTrading
  // tracked the render count 1:1.
  //
  // publicKey and isReady above already avoid this by depending on individual
  // FIELDS. This does the same for a callback: the ref always holds the current
  // objects, so behavior is identical while the identity stays stable per mode.
  const walletsRef = useRef({ local, privy, browser })

  // ─── Wake reconnect (mobile) ───
  // Backgrounding the tab lapses browser-wallet adapter sessions; on wake
  // publicKey is null and every consumer reads "connect wallet" until the
  // user does it by hand — the futures reconnect loop. If a wallet is
  // SELECTED but not connected when we come back, try once, silently.
  // Adapters that can resume do; ones that need a tap will prompt exactly
  // as a manual reconnect would. Guarded so it can never loop or throw.
  // (Placed AFTER mode/walletsRef declarations — the first cut sat above
  // them and the dep array hit the temporal dead zone: 'Cannot access
  // before initialization' on every mount. Order matters in hooks.)
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState !== 'visible') return
      if (mode !== WALLET_MODE.BROWSER) return
      const b = walletsRef.current.browser
      if (!b || b.connected || b.connecting || !b.wallet) return
      try { b.connect?.()?.catch?.(() => {}) } catch {}
    }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('pageshow', onWake)
    return () => {
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('pageshow', onWake)
    }
  }, [mode])

  walletsRef.current = { local, privy, browser }

  // Resolve signTransaction based on mode
  const signTransaction = useCallback(async (transaction) => {
    const { local, privy, browser } = walletsRef.current
    // paperwallet-20261027: no wallet of any kind signs while Paper is active.
    if (isPaperActive()) throw new Error('Paper wallet active — nothing is sent on-chain')
    if (mode === WALLET_MODE.LOCAL) {
      if (!local.publicKey || local.isLocked) {
        throw new Error('Local wallet is locked')
      }
      return local.signTransaction(transaction)
    }
    if (mode === WALLET_MODE.PRIVY) {
      if (!privy.isReady) {
        throw new Error('Bot wallet not connected')
      }
      return privy.signTransaction(transaction)
    }
    // Browser wallet
    if (!browser.publicKey) {
      throw new Error('Browser wallet not connected')
    }
    return browser.signTransaction(transaction)
  }, [mode])

  // Is any wallet ready to sign?
  const isReady = useMemo(() => {
    if (mode === WALLET_MODE.LOCAL) {
      return !!local.publicKey && !local.isLocked
    }
    if (mode === WALLET_MODE.PRIVY) {
      return privy.isReady
    }
    return !!browser.publicKey
  }, [mode, local.publicKey, local.isLocked, privy.isReady, browser.publicKey])

  const value = useMemo(() => ({
    mode,
    setMode,
    publicKey,
    signTransaction,
    isReady,
    // Expose all sources for UI
    browserWallet: browser,
    localWallet: local,
    privyWallet: privy,
    privyBlocked,
    privyBooted: bootPrivy,
    requestPrivyBoot,
  }), [mode, publicKey, signTransaction, isReady, browser, local, privy, privyBlocked, bootPrivy, requestPrivyBoot])

  return (
    <DualWalletContext.Provider value={value}>
      {/* The lazy Privy chunk. Renders nothing; its only jobs are to provide
          PrivyProvider context to its internal pump and to feed the resolved
          wallet back through onPrivyWallet. Suspense fallback is null — there's
          nothing to show while a headless runtime loads. Mounted AFTER children
          in the tree, but that's irrelevant: it paints nothing and the app
          doesn't block on it. */}
      {bootPrivy && (
        <PrivyErrorBoundary onFail={markBlocked}>
          <Suspense fallback={null}>
            <PrivyRuntime onWallet={onPrivyWallet} dropSignal={privyDropSignal} />
          </Suspense>
        </PrivyErrorBoundary>
      )}
      {privyBlocked && !blockNoticeDismissed && (
        <div style={{
          position: 'fixed', bottom: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 60, display: 'flex', alignItems: 'center', gap: 10,
          padding: '7px 12px', borderRadius: 6, maxWidth: '92vw',
          background: '#1a1410', border: '1px solid #e8a43555', color: '#e8c9a3',
          font: "500 11px/1.4 'JetBrains Mono', monospace",
        }}>
          <span>Wallet features blocked by an extension, ad blocker, or DNS filter — charts and data are unaffected.</span>
          <button
            onClick={() => setBlockNoticeDismissed(true)}
            style={{ background: 'transparent', border: 'none', color: '#e8a435', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}
          >×</button>
        </div>
      )}
      {children}
    </DualWalletContext.Provider>
  )
}

export function useDualWallet() {
  const ctx = useContext(DualWalletContext)
  if (!ctx) throw new Error('useDualWallet must be used within DualWalletProvider')
  return ctx
}